/**
 * "Try the demo" on the marketing site.
 *
 * The link is /register?lng=<lang>&demo=1: a visitor who has no inverter to connect yet should still
 * reach a dashboard with real-looking history in it. Registration always continues into the connect
 * wizard, so the only thing this has to do is carry the intent across that redirect — and, once the
 * wizard has it, preselect the demo installation instead of asking for a brand the visitor does not
 * have. Kept as two pure functions so the three places that hand the flag on cannot disagree.
 */

export const DEMO_PARAM = 'demo';

/** The provider code of the synthetic installation (sf.providers DemoProvider.ProviderCode). */
export const DEMO_PROVIDER_CODE = 'demo';

export const WIZARD_PATH = '/devices/new';

const FALSY = new Set(['0', 'false', 'no', 'off']);

/** True when this address asked for the demo — `?demo=1`, `?demo=true` or a bare `?demo`. */
export function wantsDemo(search: string | undefined | null): boolean {
  if (!search) return false;
  const value = new URLSearchParams(search).get(DEMO_PARAM);
  if (value === null) return false;
  return !FALSY.has(value.trim().toLowerCase());
}

/**
 * Where registration continues. `?lng=` is deliberately not carried along: i18next reads it once, at
 * start-up, and has stored the choice by the time anything navigates.
 */
export function wizardPathFor(search: string | undefined | null): string {
  return wantsDemo(search) ? `${WIZARD_PATH}?${DEMO_PARAM}=1` : WIZARD_PATH;
}

/** True for the connect wizard's own path, with or without its own query string — the only return path a
 *  brand-new account may land on directly, however it was created. Anything else falls back to the
 *  wizard, so a return path meant for an *existing* account (e.g. the {{portalUrl}}/billing mail link,
 *  forwarded through "Create one") cannot let a brand-new one skip onboarding or land on someone else's
 *  page. Used by `auth/returnTo.ts`'s `newAccountTarget` — the one formula shared by RegisterPage's own
 *  post-submit redirect, RedirectIfAuthenticated's /register branch and SsoCompletePage's post-accept
 *  redirect (a brand-new SSO account), so none of the three can disagree. */
export function isWizardPath(value: string): boolean {
  return value === WIZARD_PATH || value.startsWith(`${WIZARD_PATH}?`);
}
